export interface LocationData {
  // Add location fields as needed
  city?: string;
  state?: string;
  country?: string;
  latitude?: number;
  longitude?: number;
}

export interface Affiliation {
  name: string;
  affiliationBgImage: string;
  joinDate: string;
  affiliationURL: string;
  icon: string;
}

export interface Meeting {
  id?: string;
  date?: Date;
  duration?: number;
  type?: string;
}

export interface Education {
  collegeName: string;
  level: string;
  majors: string[];
  startTime?: Date;
  endTime?: Date;
  description: string;
}

export interface Work {
  companyName: string;
  position: string;
  field: string[];
  startTime?: Date;
  endTime?: Date;
  description: string;
}

export interface Activity {
  organization: string;
  position: string;
  startTime?: Date;
  endTime?: Date;
  description: string;
}

export interface QuestionResponse {
  question: string;
  response: string;
}

export interface PersonalInfo {
  id?: string; // userId (DocumentID)
  email: string;
  profileImageUrl: string;
  firstName: string;
  lastName: string;
  quote: string;
  professional: boolean;
  mentor: boolean; // read only
  locationData?: LocationData;
  educations: Education[];
  works: Work[];
  activities: Activity[];
  questionResponses: QuestionResponse[];
  interests: string[];
  links: string[];
  contacts: string[]; // read only
  rejectedProfiles: string[]; // read only
  matches: string[]; // read only
  lastActive?: Date; // read only
  createdAt?: Date;
  meetings?: Meeting[];
  referral_code?: string;
  
  // For affiliations
  affiliationInfo?: Affiliation[];
  
  userObjectives?: string[];
  userGoals?: string[];
  userSources?: string[];
  userStreaks?: string[];
  prefAIChat?: string[];
  prefMentorshipChat?: string[];
  acceptedCards?: string[];
  rejectedCards?: string[];

  // Admin-set free-form key/value preferences, fed into matching as a soft signal (SCRUM-613)
  customPreferences?: Record<string, string>;
}

// Enhanced user data combining Firebase Auth + Profile
export interface EnhancedUser {
  uid: string;

  // Firebase Auth data
  authEmail?: string;
  emailVerified?: boolean;
  disabled?: boolean;
  customClaims?: Record<string, any>;
  creationTime?: string;
  lastSignInTime?: string;

  // Profile data (from profiles collection)
  profile?: PersonalInfo;

  // Computed fields
  displayName: string;
  status: 'active' | 'inactive' | 'disabled';
  lastActiveFormatted?: string;

  // Private data (from private_user_data collection)
  privateData?: {
    mentees?: string[];
    mentors?: string[];
    utc_last_active?: number;
  };
}

// Helper functions for PersonalInfo
export const createDefaultPersonalInfo = (): PersonalInfo => ({
  email: '',
  profileImageUrl: '',
  firstName: '',
  lastName: '',
  quote: '',
  professional: false,
  mentor: false,
  educations: [],
  works: [],
  activities: [],
  questionResponses: [],
  interests: [],
  links: [],
  contacts: [],
  rejectedProfiles: [],
  matches: [],
  customPreferences: {}
});

export const isPersonalInfo = (obj: any): obj is PersonalInfo =>
  obj?.firstName?.trim() && obj?.lastName?.trim();

export type PersonalInfoUpdate = Partial<PersonalInfo>;

